// base
import { Component, ErrorInfo, ReactNode } from 'react';

// libraries
import { Box, Container, Typography, Button, Paper, Stack } from '@mui/material';
import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by error boundary:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    this.props.onReset?.();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="xl">
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4
            }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 4,
                width: '100%'
              }}
            >
              <Stack spacing={3} alignItems="center">
                <ErrorIcon color="error" sx={{ fontSize: 64 }} />

                <Typography variant="h5" component="h1" textAlign="center" color="error">
                  문제가 발생했습니다
                </Typography>

                <Typography variant="body1" color="text.secondary" textAlign="center">
                  예상치 못한 오류가 발생했습니다. 페이지를 새로고침하거나 잠시 후 다시
                  시도해주세요.
                </Typography>

                {import.meta.env.VITE_BUILD_MODE !== 'prod' && this.state.error && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      width: '100%',
                      bgcolor: 'grey.50'
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{ whiteSpace: 'pre-wrap', overflow: 'auto' }}
                    >
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack}
                    </Typography>
                  </Paper>
                )}

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={() => window.location.reload()}
                  >
                    페이지 새로고침
                  </Button>
                  <Button variant="outlined" onClick={this.handleReset}>
                    다시 시도
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}
